import React, { useState, useEffect } from 'react';
import { collection, addDoc, getDocs, deleteDoc, doc } from "firebase/firestore";
import { db } from './../../firebase/firebaseConfig';
import Layout from './Layout';
import PageTitle from '../includes/PageTitle';
import './../css/Login.css';

const AddCollectionForm = () => {
  const [collectionName, setCollectionName] = useState('');
  const [selectedSeason, setSelectedSeason] = useState(''); // Selected season
  const [year, setYear] = useState(''); // Year
  const [collections, setCollections] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchCollections = async () => {
      const collectionsCollection = collection(db, "Collections");
      const collectionSnapshot = await getDocs(collectionsCollection);
      const collectionList = collectionSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setCollections(collectionList);
    };

    const fetchSeasons = async () => {
      const seasonsCollection = collection(db, "Seasons");
      const seasonSnapshot = await getDocs(seasonsCollection);
      const seasonList = seasonSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setSeasons(seasonList);
    };

    fetchCollections();
    fetchSeasons();
  }, []);

  const validateCollectionName = (name) => {
    const trimmedName = name.trim();
    if (trimmedName === '') {
      return 'Collection name cannot be empty.';
    }
    return '';
  };

  const validateYear = (year) => {
    const currentYear = new Date().getFullYear();
    if (!year || year < 2024 || year > currentYear) {
      return 'Please enter a valid year (from 2024 to the current year).';
    }
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate collection name and year
    const nameError = validateCollectionName(collectionName);
    const yearError = validateYear(year);
    if (nameError || yearError) {
      setError(nameError || yearError);
      return;
    }

    try {
      const newCollection = await addDoc(collection(db, "Collections"), {
        collection_name: collectionName,
        season: selectedSeason || null, // If not selected, store as null
        year: year // Store year
      });
      alert(`Collection "${collectionName}" added successfully!`);
      setCollections([...collections, { id: newCollection.id, collection_name: collectionName, season: selectedSeason, year }]);
      setCollectionName('');
      setSelectedSeason('');
      setYear('');
      setError(''); // Clear any previous errors
    } catch (e) {
      console.error("Error adding collection: ", e);
      alert('Error adding collection.');
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteDoc(doc(db, "Collections", id));
      setCollections(collections.filter(collection => collection.id !== id));
      alert('Collection deleted successfully!');
    } catch (e) {
      console.error("Error deleting collection: ", e);
      alert('Error deleting collection.');
    }
  };

  return (
    <>
      <PageTitle title="Add Collection - ALMOSTINVISIBLE" />
      <Layout>
        <h1 className="unbounded">Collections</h1>
        <hr />
        <p className='unbounded fw-bold black-bordered-bottom'>Add Collection</p>
        <form onSubmit={handleSubmit}>
          <div>
            <div className="flex-column">
              <label className='nunito-2'>Collection Name*</label>
            </div>
            <div className="inputForm">
              <input
                type="text"
                value={collectionName}
                onChange={(e) => setCollectionName(e.target.value)}
                required
                className="input"
                style={{ fontWeight: "bold" }}
                placeholder='Enter the name of the collection'
              />
            </div>
            {error && <p className='error-message'>{error}</p>}
          </div>
          <br />
          <div>
            <div className="flex-column">
              <label className='nunito-2'>Season</label>
            </div>
            <div className="inputForm">
              <select
                value={selectedSeason}
                onChange={(e) => setSelectedSeason(e.target.value)}
                className="input"
              >
                <option value="">Select a season (optional)</option>
                {seasons.map(season => (
                  <option key={season.id} value={season.id}>
                    {season.season_name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <br />
          <div>
            <div className="flex-column">
              <label className='nunito-2'>Year*</label>
            </div>
            <div className="inputForm">
              <input
                type="number"
                value={year}
                onChange={(e) => setYear(e.target.value)}
                required
                min="2024"
                max={new Date().getFullYear()}
                className="input"
                style={{ fontWeight: "bold" }}
                placeholder='Enter the year'
              />
            </div>
            {error && <p className='error-message'>{error}</p>}
          </div>
          <br />
          <button type="submit" className='black-button'>Add Collection</button>
        </form>

        <hr />
        <div className='container'>
          <p className='unbounded fw-bold black-bordered-bottom'>List Of Collections</p>
          <ul>
            {collections.map((collection, index) => (
              <li key={collection.id} className='d-flex justify-content-between my-2'>
                <div>
                  <span>{index + 1}. {collection.collection_name}</span>
                  {collection.season && <span> - Season: {seasons.find(season => season.id === collection.season)?.season_name}</span>}
                  {collection.year && <span> - Year: {collection.year}</span>}
                </div>
                <button className='red-button' onClick={() => handleDelete(collection.id)}>
                  <svg stroke="currentColor" fill="currentColor" stroke-width="0" viewBox="0 0 16 16" height="1.2em" width="1.2em" xmlns="http://www.w3.org/2000/svg">
                    <path d="M5.5 5.5A.5.5 0 016 6v6a.5.5 0 01-1 0V6a.5.5 0 01.5-.5zm2.5 0a.5.5 0 01.5.5v6a.5.5 0 01-1 0V6a.5.5 0 01.5-.5zm3 .5a.5.5 0 00-1 0v6a.5.5 0 001 0V6z"></path>
                    <path fill-rule="evenodd" d="M14.5 3a1 1 0 01-1 1H13v9a2 2 0 01-2 2H5a2 2 0 01-2-2V4h-.5a1 1 0 01-1-1V2a1 1 0 011-1H6a1 1 0 011-1h2a1 1 0 011 1h3.5a1 1 0 011 1v1zM4.118 4L4 4.059V13a1 1 0 001 1h6a1 1 0 001-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z" clip-rule="evenodd"></path>
                  </svg>
                  &nbsp; Delete
                </button>
              </li>
            ))}
          </ul>
        </div>
      </Layout>
    </>
  );
};

export default AddCollectionForm;
